/**
 * Voting Analysis notification banner.
 *
 * Sits directly under the hero, teal-bordered, with a CTA to the voting
 * analysis page. Dismissal is persisted once-per-browser in `localStorage`.
 */
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'voting-analysis-banner-dismissed';

export function VotingAnalysisBanner(): JSX.Element | null {
  // `dismissed` true = remove from DOM entirely, false = render.
  // `visible` drives the fade-in transform animation.
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return true;
    return window.localStorage.getItem(STORAGE_KEY) === 'true';
  });
  const [visible, setVisible] = useState(false);
  const fadeOutTimer = useRef<number | null>(null);

  useEffect(() => {
    if (dismissed) return;
    // 300ms delay before fade-in.
    const t = window.setTimeout(() => setVisible(true), 300);
    return () => {
      window.clearTimeout(t);
      if (fadeOutTimer.current !== null) {
        window.clearTimeout(fadeOutTimer.current);
        fadeOutTimer.current = null;
      }
    };
  }, [dismissed]);

  if (dismissed) return null;

  const handleClose = (e: React.MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    window.localStorage.setItem(STORAGE_KEY, 'true');
    setVisible(false);
    // 500ms fade-out before removal from the DOM.
    fadeOutTimer.current = window.setTimeout(() => setDismissed(true), 500);
  };

  // Styling aligned to the brand `--accent` token (#4EB7AC) so the banner
  // teal matches the hero/sidebar/buttons elsewhere on the page.
  // Background is the brand `--accent-ultra-light`, text uses `--accent-hover`
  // for adequate contrast on the mint surface.
  const containerClass =
    'relative bg-[color:var(--accent-ultra-light)] border-l-[3px] border-[color:var(--accent)] px-6 py-3 m-0 z-[100] ' +
    'transition-[opacity,transform] duration-[400ms] ease-out ' +
    (visible ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2.5 pointer-events-none');

  return (
    <div id="voting-analysis-banner" className={containerClass}>
      <div className="max-w-[1200px] mx-auto flex items-center justify-between gap-4">
        <div className="flex-1">
          <p className="font-[var(--font-body)] text-[0.9rem] leading-[1.5] m-0 text-[color:var(--accent-hover)]">
            New: Explore Real-Time FOMC voting patterns, dissents, and policy evolution analysis.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/*
            Filled (teal bg, WHITE text) at rest so the CTA reads as a button.
            Hover reverses (transparent bg, accent text).
          */}
          <Link
            to="/central-bank/voting-analysis"
            className="
              cursor-pointer bg-[color:var(--accent)] text-white px-4 py-1.5 rounded
              no-underline font-[var(--font-body)] text-[0.85rem] font-medium
              border border-[color:var(--accent)] transition-all duration-200 whitespace-nowrap
              hover:bg-[color:var(--accent-hover)] hover:border-[color:var(--accent-hover)]
            "
          >
            View Voting Analysis
          </Link>
          <button
            type="button"
            onClick={handleClose}
            aria-label="Close banner"
            className="
              bg-transparent text-[#64748b] border-none text-[1.25rem] cursor-pointer
              p-1 opacity-60 transition-opacity duration-200 leading-none shrink-0
              hover:opacity-100
            "
          >
            &times;
          </button>
        </div>
      </div>
    </div>
  );
}

export default VotingAnalysisBanner;
